import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { 
  ArrowLeft,
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  ExternalLink, 
  FileText, 
  Info,
  Maximize, 
  Minimize,
  Play, 
  X,
  Activity,
  Moon,
  Apple
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Key takeaway component for highlighting important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative bg-gradient-to-r from-green-50 via-blue-50 to-slate-50 border border-green-200 rounded-lg p-3 my-4 overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-500 to-blue-600"></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-5 h-5 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-2.5 w-2.5 text-white" />
          </div>
        </div>
        <div className="ml-2">
          <h4 className="text-xs font-bold text-slate-900 mb-1 tracking-tight">Key Clinical Insight</h4>
          <p className="text-slate-700 leading-relaxed text-xs">{children}</p>
        </div>
      </div>
    </div>
  );
};

// Evidence box component
const EvidenceBox = ({ title, strength, children }: { title: string; strength: string; children: React.ReactNode }) => {
  const getStrengthColor = (strength: string) => {
    if (strength.includes('★★★★')) return 'border-green-500 bg-green-50';
    if (strength.includes('★★★')) return 'border-blue-500 bg-blue-50';
    if (strength.includes('★★')) return 'border-yellow-500 bg-yellow-50';
    return 'border-slate-500 bg-slate-50';
  };

  return (
    <div className={`border rounded-lg p-3 ${getStrengthColor(strength)}`}>
      <div className="flex justify-between items-start mb-1">
        <h5 className="text-xs font-semibold text-slate-800">{title}</h5>
        <span className="text-xs text-slate-600 ml-2">{strength}</span>
      </div>
      <div className="text-xs text-slate-700">{children}</div>
    </div>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef} 
      className="w-full bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden mb-8"
    >
      <div className="p-3 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-slate-50 flex justify-between items-center">
        <h3 className="text-sm font-medium text-slate-800">Lifestyle Foundations 4.0 Presentation</h3>
        <div className="text-xs text-slate-500 font-medium">
          Slide {currentSlide + 1} of {slides.length}
        </div>
      </div>
      
      <div className="p-4 md:p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-3 tracking-tight leading-tight">{slides[currentSlide].title}</h2>
        <div className="mb-4 min-h-[600px] overflow-y-auto max-h-[700px] pr-2 thin-scrollbar">
          {slides[currentSlide].content}
        </div>
      </div>
      
      <div className="px-4 md:px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <button 
          onClick={prevSlide}
          className="flex items-center text-blue-600 hover:text-blue-800 text-xs font-medium transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </button>
        
        <div className="flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={index === currentSlide 
                ? "h-2 w-2 rounded-full bg-blue-600 shadow-sm" 
                : "h-2 w-2 rounded-full bg-slate-300 hover:bg-slate-400 transition-colors"}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <button 
          onClick={nextSlide}
          className="flex items-center text-blue-600 hover:text-blue-800 text-xs font-medium transition-colors"
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

// SVG Diagram components for the lecture slides in Cell/Nature journal style
const TitleDiagram = () => (
  <svg viewBox="0 0 1000 450" className="w-full h-auto max-w-4xl mx-auto my-6">
    <defs>
      <linearGradient id="title-bg-4" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f8fafc" />
        <stop offset="100%" stopColor="#f1f5f9" />
      </linearGradient>
      <filter id="title-shadow-4" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.08"/>
      </filter>
    </defs>
    <rect width="1000" height="450" fill="url(#title-bg-4)" rx="15" ry="15" filter="url(#title-shadow-4)" />
    
    {/* Main title structure */}
    <g transform="translate(500, 225)">
      {/* Central foundation symbol */}
      <circle cx="0" cy="0" r="80" fill="#3b82f6" fillOpacity="0.1" stroke="#2563eb" strokeWidth="4" />
      <text x="0" y="-8" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="16" fontWeight="bold" fill="#1e40af">
        Lifestyle
      </text>
      <text x="0" y="12" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="16" fontWeight="bold" fill="#1e40af">
        Foundations 4.0
      </text>
      
      {/* Version indicator */}
      <circle cx="60" cy="-60" r="15" fill="#ef4444" />
      <text x="60" y="-55" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="bold" fill="white">
        4.0
      </text>
      
      {/* Three foundational pillars around the circle */}
      <g opacity="0.8">
        {/* Nutrition */}
        <circle cx="-140" cy="-40" r="35" fill="#22c55e" fillOpacity="0.15" stroke="#16a34a" strokeWidth="3" />
        <text x="-140" y="-35" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="bold" fill="#15803d">
          Nutrition
        </text>
        <text x="-140" y="-20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#15803d">
          Metabolic
        </text>
        <text x="-140" y="-8" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#15803d">
          Sensing
        </text>
        
        {/* Exercise */}
        <circle cx="140" cy="-40" r="35" fill="#f59e0b" fillOpacity="0.15" stroke="#d97706" strokeWidth="3" />
        <text x="140" y="-35" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="bold" fill="#92400e">
          Exercise
        </text>
        <text x="140" y="-20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#92400e">
          Cellular
        </text>
        <text x="140" y="-8" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#92400e">
          Activation
        </text>
        
        {/* Sleep */}
        <circle cx="0" cy="110" r="35" fill="#8b5cf6" fillOpacity="0.15" stroke="#7c3aed" strokeWidth="3" />
        <text x="0" y="105" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="bold" fill="#5b21b6">
          Sleep
        </text>
        <text x="0" y="120" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#5b21b6">
          Circadian
        </text>
        <text x="0" y="132" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#5b21b6">
          Optimization
        </text>
      </g>
      
      {/* Connecting pathways */}
      <line x1="-105" y1="-40" x2="-80" y2="-20" stroke="#64748b" strokeWidth="2" strokeDasharray="4,4" opacity="0.6" />
      <line x1="105" y1="-40" x2="80" y2="-20" stroke="#64748b" strokeWidth="2" strokeDasharray="4,4" opacity="0.6" />
      <line x1="0" y1="75" x2="0" y2="80" stroke="#64748b" strokeWidth="2" strokeDasharray="4,4" opacity="0.6" />
    </g>
    
    {/* Header text */}
    <text x="500" y="60" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="20" fontWeight="bold" fill="#334155">
      Lifestyle Interventions for Longevity and Health Optimization
    </text>
    
    {/* Subtitle */}
    <text x="500" y="85" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="16" fill="#64748b">
      A SHIFT Framework Approach - Advanced Clinical Applications
    </text>
    
    {/* Bottom tagline */}
    <text x="500" y="400" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="medium" fill="#64748b">
      Evidence-Based Protocols for Healthspan Optimization
    </text>
  </svg>
);

const ParadigmEvolutionDiagram = () => (
  <svg viewBox="0 0 1100 500" className="w-full h-auto max-w-4xl mx-auto my-6">
    <defs>
      <linearGradient id="evolution-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fefefe" />
        <stop offset="100%" stopColor="#f8fafc" />
      </linearGradient>
      <filter id="evolution-shadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.08"/>
      </filter>
    </defs>
    
    {/* Background */}
    <rect width="1100" height="500" fill="url(#evolution-bg)" rx="10" ry="10" filter="url(#evolution-shadow)" />
    
    {/* Title */}
    <text x="550" y="50" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="18" fontWeight="bold" fill="#334155">
      Evolution of Medical Paradigms: Towards Longevity Medicine
    </text>
    
    {/* Timeline with three stages */}
    <line x1="100" y1="250" x2="1000" y2="250" stroke="#64748b" strokeWidth="3" />
    
    {/* Stage 1: Disease Treatment */}
    <g transform="translate(200, 250)">
      <circle cx="0" cy="0" r="8" fill="#ef4444" />
      <rect x="-120" y="-80" width="240" height="140" rx="12" ry="12" fill="#fef2f2" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-50" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="bold" fill="#dc2626">
        Traditional Medicine
      </text>
      <text x="0" y="-30" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fill="#991b1b">
        Reactive Disease Treatment
      </text>
      
      <circle cx="-60" cy="10" r="12" fill="#fca5a5" stroke="#dc2626" strokeWidth="1.5" />
      <text x="-60" y="15" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#7f1d1d">
        Symptoms
      </text>
      
      <circle cx="0" cy="10" r="12" fill="#fca5a5" stroke="#dc2626" strokeWidth="1.5" />
      <text x="0" y="15" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#7f1d1d">
        Disease
      </text>
      
      <circle cx="60" cy="10" r="12" fill="#fca5a5" stroke="#dc2626" strokeWidth="1.5" />
      <text x="60" y="15" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#7f1d1d">
        Treatment
      </text>
      
      <text x="0" y="110" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#7f1d1d">
        20th Century
      </text>
    </g>
    
    {/* Stage 2: Preventive Medicine */}
    <g transform="translate(550, 250)">
      <circle cx="0" cy="0" r="8" fill="#f59e0b" />
      <rect x="-120" y="-80" width="240" height="140" rx="12" ry="12" fill="#fefbeb" stroke="#f59e0b" strokeWidth="2" />
      <text x="0" y="-50" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="bold" fill="#d97706">
        Preventive Medicine
      </text>
      <text x="0" y="-30" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fill="#92400e">
        Risk Factor Management
      </text>
      
      <circle cx="-60" cy="10" r="12" fill="#fcd34d" stroke="#d97706" strokeWidth="1.5" />
      <text x="-60" y="15" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#78350f">
        Screening
      </text>
      
      <circle cx="0" cy="10" r="12" fill="#fcd34d" stroke="#d97706" strokeWidth="1.5" />
      <text x="0" y="15" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#78350f">
        Prevention
      </text>
      
      <circle cx="60" cy="10" r="12" fill="#fcd34d" stroke="#d97706" strokeWidth="1.5" />
      <text x="60" y="15" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#78350f">
        Lifestyle
      </text>
      
      <text x="0" y="110" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#78350f">
        Early 21st Century
      </text>
    </g>
    
    {/* Stage 3: Longevity Medicine */}
    <g transform="translate(900, 250)">
      <circle cx="0" cy="0" r="8" fill="#22c55e" />
      <rect x="-120" y="-80" width="240" height="140" rx="12" ry="12" fill="#f0fdf4" stroke="#22c55e" strokeWidth="2" />
      <text x="0" y="-50" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="bold" fill="#16a34a">
        Longevity Medicine
      </text>
      <text x="0" y="-30" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fill="#15803d">
        Healthspan Optimization
      </text>
      
      <circle cx="-60" cy="10" r="12" fill="#86efac" stroke="#16a34a" strokeWidth="1.5" />
      <text x="-60" y="15" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#14532d">
        Vitality
      </text>
      
      <circle cx="0" cy="10" r="12" fill="#86efac" stroke="#16a34a" strokeWidth="1.5" />
      <text x="0" y="15" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#14532d">
        Resilience
      </text>
      
      <circle cx="60" cy="10" r="12" fill="#86efac" stroke="#16a34a" strokeWidth="1.5" />
        <text x="60" y="15" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#14532d">
        Longevity
      </text>
      
      <text x="0" y="110" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#14532d">
        Present & Future
      </text>
    </g>
    
    {/* Arrows between stages */}
    <path d="M340 250 L410 250" stroke="#3b82f6" strokeWidth="4" fill="none" markerEnd="url(#arrow-evo)" />
    <path d="M690 250 L760 250" stroke="#3b82f6" strokeWidth="4" fill="none" markerEnd="url(#arrow-evo)" />
    <defs>
      <marker id="arrow-evo" markerWidth="12" markerHeight="12" refX="11" refY="4" orient="auto" markerUnits="strokeWidth">
        <path d="M0,0 L0,8 L12,4 z" fill="#3b82f6"/>
      </marker>
    </defs>
    
    {/* Bottom description */}
    <text x="550" y="420" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="medium" fill="#64748b">
      From reactive treatment to proactive optimization of biological aging processes
    </text>
    <text x="550" y="445" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="medium" fill="#64748b">
      Lifestyle medicine as the foundation for extending healthspan and vitality
    </text>
  </svg>
);

const ShiftFrameworkDiagram = () => (
  <svg viewBox="0 0 900 600" className="w-full h-auto max-w-4xl mx-auto my-6">
    <defs>
      <linearGradient id="shift-bg-4" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fefefe" />
        <stop offset="100%" stopColor="#f8fafc" />
      </linearGradient>
      <filter id="shift-shadow-4" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.08"/>
      </filter>
    </defs>
    
    {/* Background */}
    <rect width="900" height="600" fill="url(#shift-bg-4)" rx="10" ry="10" filter="url(#shift-shadow-4)" />
    
    {/* Title */}
    <text x="450" y="50" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="18" fontWeight="bold" fill="#334155">
      SHIFT Framework 4.0: Mechanistic Understanding of Lifestyle Impact
    </text>
    
    {/* Pentagon shape with SHIFT components */}
    <g transform="translate(450, 300)">
      {/* Pentagon outline */}
      <polygon points="0,-160 152,-49 94,129 -94,129 -152,-49" fill="#f8fafc" stroke="#475569" strokeWidth="4" />
      
      {/* Central circle - Lifestyle Interventions */}
      <circle cx="0" cy="0" r="75" fill="white" stroke="#1e40af" strokeWidth="4" />
      <text x="0" y="-18" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="bold" fill="#1e40af">
        Lifestyle 4.0
      </text>
      <text x="0" y="-2" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="bold" fill="#1e40af">
        Interventions
      </text>
      <text x="0" y="18" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fill="#1e40af">
        Evidence-Based
      </text>
      <text x="0" y="32" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fill="#1e40af">
        Clinical Protocols
      </text>
      
      {/* S - Sensing */}
      <g transform="translate(0, -160)">
        <circle cx="0" cy="0" r="60" fill="#dbeafe" stroke="#3b82f6" strokeWidth="3" />
        <text x="0" y="-20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="20" fontWeight="bold" fill="#1e40af">
          S
        </text>
        <text x="0" y="-3" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#1e40af">
          Sensing &
        </text>
        <text x="0" y="12" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#1e40af">
          Fuel-Gauging
        </text>
        <text x="0" y="-90" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          IIS/IGF-1, mTOR, AMPK, Sirtuins
        </text>
        <text x="0" y="-78" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          Metabolic flexibility
        </text>
      </g>
      
      {/* H - Homeostatic Defence */}
      <g transform="translate(152, -49)">
        <circle cx="0" cy="0" r="60" fill="#dcfce7" stroke="#22c55e" strokeWidth="3" />
        <text x="0" y="-20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="20" fontWeight="bold" fill="#15803d">
          H
        </text>
        <text x="0" y="-3" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#15803d">
          Homeostatic
        </text>
        <text x="0" y="12" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#15803d">
          Defence & Repair
        </text>
        <text x="0" y="-90" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          NRF2, p53/p16, Autophagy
        </text>
        <text x="0" y="-78" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          Cellular resilience
        </text>
      </g>
      
      {/* I - Inflammation */}
      <g transform="translate(94, 129)">
        <circle cx="0" cy="0" r="60" fill="#fef3c7" stroke="#f59e0b" strokeWidth="3" />
        <text x="0" y="-20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="20" fontWeight="bold" fill="#d97706">
          I
        </text>
        <text x="0" y="-3" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#d97706">
          Inflammation &
        </text>
        <text x="0" y="12" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#d97706">
          Immune Tone
        </text>
        <text x="0" y="88" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          NF-κB, JAK-STAT, SASP
        </text>
        <text x="0" y="100" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          Inflammaging control
        </text>
      </g>
      
      {/* F - Fibrosis */}
      <g transform="translate(-94, 129)">
        <circle cx="0" cy="0" r="60" fill="#fce7f3" stroke="#ec4899" strokeWidth="3" />
        <text x="0" y="-20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="20" fontWeight="bold" fill="#be185d">
          F
        </text>
        <text x="0" y="-3" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#be185d">
          Fibrosis /
        </text>
        <text x="0" y="12" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#be185d">
          Remodeling
        </text>
        <text x="0" y="88" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          TGF-β, WNT, ECM
        </text>
        <text x="0" y="100" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          Tissue integrity
        </text>
      </g>
      
      {/* T - Timekeeping */}
      <g transform="translate(-152, -49)">
        <circle cx="0" cy="0" r="60" fill="#ede9fe" stroke="#8b5cf6" strokeWidth="3" />
        <text x="0" y="-20" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="20" fontWeight="bold" fill="#7c3aed">
          T
        </text>
        <text x="0" y="-3" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#7c3aed">
          Timekeeping &
        </text>
        <text x="0" y="12" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fill="#7c3aed">
          Chronobiology
        </text>
        <text x="0" y="-90" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          CLOCK/BMAL1, Epigenetic clocks
        </text>
        <text x="0" y="-78" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="8" fill="#64748b">
          Circadian optimization
        </text>
      </g>
    </g>
    
    {/* Bottom description */}
    <text x="450" y="520" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="medium" fill="#64748b">
      Advanced mechanistic framework for understanding lifestyle interventions
    </text>
    <text x="450" y="545" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="medium" fill="#64748b">
      Each pillar represents targetable pathways for clinical optimization
    </text>
  </svg>
);

const SensingPathwaysDiagram = () => (
  <svg viewBox="0 0 1200 600" className="w-full h-auto max-w-4xl mx-auto my-6">
    <defs>
      <linearGradient id="sensing-bg-4" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fefefe" />
        <stop offset="100%" stopColor="#f8fafc" />
      </linearGradient>
      <filter id="sensing-shadow-4" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.08"/>
      </filter>
    </defs>
    
    {/* Background */}
    <rect width="1200" height="600" fill="url(#sensing-bg-4)" rx="10" ry="10" filter="url(#sensing-shadow-4)" />
    
    {/* Title */}
    <text x="600" y="50" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="18" fontWeight="bold" fill="#334155">
      Sensing & Metabolic Fuel-Gauging: Core Pathways
    </text>
    <text x="600" y="75" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fill="#64748b">
      Nutrient-Sensing Networks That Determine Cellular Fate
    </text>
    
    {/* Central pathway network */}
    <g transform="translate(600, 320)">
      {/* Nutrient abundance side (left) */}
      <g transform="translate(-250, 0)">
        <rect x="-150" y="-120" width="300" height="240" rx="15" fill="#fef2f2" stroke="#ef4444" strokeWidth="3" />
        <text x="0" y="-90" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="16" fontWeight="bold" fill="#dc2626">
          Nutrient Abundance
        </text>
        <text x="0" y="-70" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fill="#991b1b">
          Growth & Storage Signals
        </text>
        
        {/* IIS pathway */}
        <rect x="-100" y="-40" width="80" height="40" rx="8" fill="#fca5a5" stroke="#dc2626" strokeWidth="2" />
        <text x="-60" y="-25" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="bold" fill="#7f1d1d">
          IIS/IGF-1
        </text>
        <text x="-60" y="-10" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="9" fill="#7f1d1d">
          Growth
        </text>
        
        {/* mTOR pathway */}
        <rect x="20" y="-40" width="80" height="40" rx="8" fill="#fca5a5" stroke="#dc2626" strokeWidth="2" />
        <text x="60" y="-25" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="bold" fill="#7f1d1d">
          mTOR
        </text>
        <text x="60" y="-10" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="9" fill="#7f1d1d">
          Anabolism
        </text>
        
        {/* Outcomes */}
        <text x="0" y="40" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="medium" fill="#7f1d1d">
          ↑ Protein synthesis
        </text>
        <text x="0" y="55" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="medium" fill="#7f1d1d">
          ↑ Cell proliferation
        </text>
        <text x="0" y="70" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="medium" fill="#7f1d1d">
          ↓ Autophagy
        </text>
        <text x="0" y="85" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="medium" fill="#7f1d1d">
          ↑ Aging rate
        </text>
      </g>
      
      {/* Nutrient scarcity side (right) */}
      <g transform="translate(250, 0)">
        <rect x="-150" y="-120" width="300" height="240" rx="15" fill="#f0fdf4" stroke="#22c55e" strokeWidth="3" />
        <text x="0" y="-90" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="16" fontWeight="bold" fill="#16a34a">
          Nutrient Scarcity/Stress
        </text>
        <text x="0" y="-70" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fill="#15803d">
          Maintenance & Longevity Signals
        </text>
        
        {/* AMPK pathway */}
        <rect x="-100" y="-40" width="80" height="40" rx="8" fill="#86efac" stroke="#16a34a" strokeWidth="2" />
        <text x="-60" y="-25" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="bold" fill="#14532d">
          AMPK
        </text>
        <text x="-60" y="-10" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="9" fill="#14532d">
          Energy sensor
        </text>
        
        {/* Sirtuins pathway */}
        <rect x="20" y="-40" width="80" height="40" rx="8" fill="#86efac" stroke="#16a34a" strokeWidth="2" />
        <text x="60" y="-25" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="bold" fill="#14532d">
          Sirtuins
        </text>
        <text x="60" y="-10" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="9" fill="#14532d">
          NAD+ dependent
        </text>
        
        {/* Outcomes */}
        <text x="0" y="40" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="medium" fill="#14532d">
          ↑ Autophagy
        </text>
        <text x="0" y="55" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="medium" fill="#14532d">
          ↑ Stress resistance
        </text>
        <text x="0" y="70" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="medium" fill="#14532d">
          ↑ DNA repair
        </text>
        <text x="0" y="85" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="medium" fill="#14532d">
          ↑ Longevity
        </text>
      </g>
      
      {/* Central balance point */}
      <circle cx="0" cy="0" r="40" fill="white" stroke="#64748b" strokeWidth="3" />
      <text x="0" y="-8" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="bold" fill="#475569">
        Metabolic
      </text>
      <text x="0" y="8" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="bold" fill="#475569">
        Switch
      </text>
      
      {/* Balance beam */}
      <rect x="-200" y="-8" width="400" height="16" rx="8" fill="#64748b" opacity="0.3" />
      
      {/* Arrows showing the balance */}
      <path d="M-180 0 L-80 0" stroke="#ef4444" strokeWidth="3" fill="none" markerEnd="url(#arrow-red)" />
      <path d="M180 0 L80 0" stroke="#22c55e" strokeWidth="3" fill="none" markerEnd="url(#arrow-green)" />
    </g>
    
    <defs>
      <marker id="arrow-red" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
        <path d="M0,0 L0,6 L9,3 z" fill="#ef4444"/>
      </marker>
      <marker id="arrow-green" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
        <path d="M0,0 L0,6 L9,3 z" fill="#22c55e"/>
      </marker>
    </defs>
    
    {/* Bottom description */}
    <text x="600" y="540" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="medium" fill="#64748b">
      Lifestyle interventions modulate this fundamental metabolic switch
    </text>
    <text x="600" y="560" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="medium" fill="#64748b">
      Strategic activation of longevity pathways through nutrition, exercise, and sleep
    </text>
  </svg>
);

// Define all slides for the complete presentation
const slides: Slide[] = [
  // Slide 1: Title Slide
  {
    id: 1,
    title: "Lifestyle Foundations 4.0: Advanced Clinical Applications",
    content: (
      <>
        <div className="text-center mb-4">
          <TitleDiagram />
        </div>
        
        <div className="text-center mb-4">
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Evidence-Based Protocols for Healthspan Optimization</h3>
          <p className="text-xs text-slate-600 mb-3">A SHIFT Framework Approach to Longevity Medicine</p>
        </div>
        
        <div className="bg-slate-50 p-3 rounded-lg border">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <h4 className="text-xs font-semibold text-slate-800 mb-1">Target Audience</h4>
              <p className="text-xs text-slate-700">Physicians new to longevity medicine</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-800 mb-1">Duration</h4>
              <p className="text-xs text-slate-700">Approximately 30 minutes</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-800 mb-1">Version</h4>
              <p className="text-xs text-slate-700">4.0 - Latest evidence synthesis</p>
            </div>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 2: The Evolution of Medical Paradigms
  {
    id: 2,
    title: "The Evolution of Medical Paradigms: From Treatment to Optimization",
    content: (
      <>
        <ParadigmEvolutionDiagram />
        
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div className="bg-red-50 p-3 rounded-lg border border-red-200">
            <h4 className="text-xs font-semibold text-red-800 mb-2">Traditional Medicine</h4>
            <ul className="text-xs text-red-700 space-y-1">
              <li>• Reactive disease treatment</li>
              <li>• Symptom management</li>
              <li>• Single-disease focus</li>
              <li>• Limited prevention</li>
            </ul>
          </div>
          
          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <h4 className="text-xs font-semibold text-yellow-800 mb-2">Preventive Medicine</h4>
            <ul className="text-xs text-yellow-700 space-y-1">
              <li>• Risk factor management</li>
              <li>• Screening programs</li>
              <li>• Basic lifestyle advice</li>
              <li>• Population health focus</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <h4 className="text-xs font-semibold text-green-800 mb-2">Longevity Medicine</h4>
            <ul className="text-xs text-green-700 space-y-1">
              <li>• Healthspan optimization</li>
              <li>• Mechanistic interventions</li>
              <li>• Precision protocols</li>
              <li>• Biological age reversal</li>
            </ul>
          </div>
        </div>
        
        <KeyTakeaway>
          Longevity medicine represents a paradigm shift from treating disease to optimizing biological age and extending the period of healthy life.
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 3: SHIFT Framework 4.0 Overview
  {
    id: 3,
    title: "SHIFT Framework 4.0: Mechanistic Understanding of Lifestyle Impact",
    content: (
      <>
        <ShiftFrameworkDiagram />
        
        <div className="mt-4">
          <h4 className="text-xs font-semibold text-slate-800 mb-2">Framework Components (Version 4.0)</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="bg-blue-50 p-2 rounded border border-blue-200">
                <h5 className="text-xs font-semibold text-blue-800">S - Sensing & Fuel-Gauging</h5>
                <p className="text-xs text-blue-700">IIS/IGF-1, mTOR, AMPK, Sirtuins</p>
              </div>
              
              <div className="bg-green-50 p-2 rounded border border-green-200">
                <h5 className="text-xs font-semibold text-green-800">H - Homeostatic Defence</h5>
                <p className="text-xs text-green-700">NRF2, p53/p16, Autophagy</p>
              </div>
              
              <div className="bg-yellow-50 p-2 rounded border border-yellow-200">
                <h5 className="text-xs font-semibold text-yellow-800">I - Inflammation & Immune</h5>
                <p className="text-xs text-yellow-700">NF-κB, JAK-STAT, SASP</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="bg-pink-50 p-2 rounded border border-pink-200">
                <h5 className="text-xs font-semibold text-pink-800">F - Fibrosis & Remodeling</h5>
                <p className="text-xs text-pink-700">TGF-β, WNT, ECM Cross-linking</p>
              </div>
              
              <div className="bg-purple-50 p-2 rounded border border-purple-200">
                <h5 className="text-xs font-semibold text-purple-800">T - Timekeeping & Chronobiology</h5>
                <p className="text-xs text-purple-700">CLOCK/BMAL1, Epigenetic clocks</p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 4: Learning Objectives for Clinicians
  {
    id: 4,
    title: "What You Will Master: Evidence-Based Clinical Applications",
    content: (
      <>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-3 flex items-center">
              <CheckCircle2 className="h-4 w-4 text-green-600 mr-2" />
              Core Learning Outcomes
            </h4>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <p className="text-xs font-medium text-slate-800">Mechanistic Understanding</p>
                  <p className="text-xs text-slate-600">How lifestyle interventions modulate aging pathways at the cellular level</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <p className="text-xs font-medium text-slate-800">Clinical Protocols</p>
                  <p className="text-xs text-slate-600">Evidence-based intervention strategies with specific dosing and timing</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <p className="text-xs font-medium text-slate-800">Biomarker Monitoring</p>
                  <p className="text-xs text-slate-600">Key metrics for tracking intervention efficacy and patient progress</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <p className="text-xs font-medium text-slate-800">Personalization Strategies</p>
                  <p className="text-xs text-slate-600">Tailoring interventions based on individual patient characteristics</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-3 flex items-center">
              <Activity className="h-4 w-4 text-blue-600 mr-2" />
              Clinical Applications
            </h4>
            <div className="bg-gradient-to-br from-blue-50 to-green-50 p-3 rounded-lg border">
              <div className="space-y-2">
                <div className="flex items-center text-xs">
                  <Apple className="h-3 w-3 text-green-600 mr-2" />
                  <span className="font-medium">Precision Nutrition</span>
                </div>
                <p className="text-xs text-slate-700 ml-5">CR, IF, ketogenic protocols, chrononutrition</p>
                
                <div className="flex items-center text-xs">
                  <Activity className="h-3 w-3 text-orange-600 mr-2" />
                  <span className="font-medium">Exercise Prescription</span>
                </div>
                <p className="text-xs text-slate-700 ml-5">HIIT, resistance training, Zone 2, NEAT optimization</p>
                
                <div className="flex items-center text-xs">
                  <Moon className="h-3 w-3 text-purple-600 mr-2" />
                  <span className="font-medium">Sleep Optimization</span>
                </div>
                <p className="text-xs text-slate-700 ml-5">Circadian alignment, sleep hygiene, chronotherapy</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 bg-slate-50 p-3 rounded-lg border">
          <h5 className="text-xs font-semibold text-slate-800 mb-1">Evidence Quality Standards</h5>
          <p className="text-xs text-slate-700">All recommendations based on peer-reviewed research with clear evidence grading (★★★★ = strong evidence, ★★★ = moderate, ★★ = limited)</p>
        </div>
      </>
    )
  },
  
  // Slide 5: Sensing & Metabolic Fuel-Gauging Overview
  {
    id: 5,
    title: "S: Sensing & Metabolic Fuel-Gauging - The Master Switch",
    content: (
      <>
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-blue-800 mb-1">Core Pathways That Determine Cellular Fate</h3>
          <p className="text-xs text-slate-700">Fundamental networks responding to nutrient availability and energy status</p>
        </div>
        
        <SensingPathwaysDiagram />
        
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Growth/Storage Pathways</h4>
            <div className="space-y-2">
              <EvidenceBox title="Insulin/IGF-1 Signaling (IIS)" strength="★★★★">
                Promotes growth, metabolism. Downregulation linked to longevity across species.
              </EvidenceBox>
              
              <EvidenceBox title="mTOR (mechanistic Target of Rapamycin)" strength="★★★★">
                Central nutrient sensor. Inhibition extends lifespan in multiple model organisms.
              </EvidenceBox>
            </div>
          </div>
          
          <div>
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Longevity Pathways</h4>
            <div className="space-y-2">
              <EvidenceBox title="AMPK (AMP-activated Protein Kinase)" strength="★★★★">
                Cellular energy sensor. Activation promotes stress resistance and longevity.
              </EvidenceBox>
              
              <EvidenceBox title="Sirtuins (SIRT1-7)" strength="★★★★">
                NAD+-dependent deacetylases. Regulate metabolism, stress resistance, aging.
              </EvidenceBox>
            </div>
          </div>
        </div>
        
        <KeyTakeaway>
          The balance between growth/storage and maintenance/longevity pathways is fundamental to aging. Lifestyle interventions can strategically modulate this balance.
        </KeyTakeaway>
      </>
    )
  }
  
  // Note: This is showing the first 5 slides of what would be a complete 20+ slide presentation
  // The remaining slides would cover:
  // - Slides 6-8: Detailed nutritional interventions (CR, IF, Ketogenic, Omega-3s, meal timing)
  // - Slides 9-11: Exercise modulation of sensing pathways (HIIT, resistance, endurance, NEAT)
  // - Slides 12-14: Sleep and chronobiology impacts
  // - Slides 15-17: Clinical protocols and biomarker monitoring
  // - Slides 18-20: Integration strategies and personalization
  // - Slides 21-23: Future directions and clinical implementation
];

const LifestyleFoundations4 = () => {
  const [_, setLocation] = useLocation();

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        
        <main className="container mx-auto px-4 py-6 flex-grow max-w-4xl">
          <div className="flex items-center mb-5">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setLocation('/mentorship-content')}
              className="text-slate-600 hover:text-slate-900 mr-3"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            
            <h1 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight">
              Lifestyle Foundations 4.0
            </h1>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 mb-6">
            <p className="text-xs text-slate-700 leading-relaxed mb-2">
              Advanced clinical applications of lifestyle interventions for longevity and health optimization. 
              This comprehensive presentation explores evidence-based protocols through the SHIFT framework, 
              providing mechanistic insights and practical clinical guidance.
            </p>

            <p className="text-xs text-slate-700 leading-relaxed">
              Designed for physicians new to longevity medicine, featuring the latest research on nutrition, 
              exercise, sleep, and their impacts on cellular aging pathways.
            </p>
          </div>
          
          <Slideshow slides={slides} />
          
          <div className="mt-6 space-y-4">
            <div>
              <h2 className="text-sm font-semibold mb-2 text-slate-900">Clinical Resources</h2>
              <div className="grid md:grid-cols-2 gap-3">
                <Card className="p-3 border border-slate-200 shadow-sm bg-white">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5 mr-2">
                      <div className="w-6 h-6 bg-blue-50 rounded-full flex items-center justify-center">
                        <FileText className="h-3 w-3 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xs font-medium mb-1 text-slate-900">Evidence Tables</h3>
                      <p className="text-xs text-slate-600 mb-1">
                        Complete intervention protocols with evidence grades and biomarkers.
                      </p>
                      <Button variant="outline" size="sm" disabled className="text-xs h-6 px-2">
                        Coming Soon
                      </Button>
                    </div>
                  </div>
                </Card>

                <Card className="p-3 border border-slate-200 shadow-sm bg-white">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5 mr-2">
                      <div className="w-6 h-6 bg-blue-50 rounded-full flex items-center justify-center">
                        <Activity className="h-3 w-3 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xs font-medium mb-1 text-slate-900">Clinical Calculators</h3>
                      <p className="text-xs text-slate-600 mb-1">
                        Interactive tools for personalized intervention planning.
                      </p>
                      <Button variant="outline" size="sm" disabled className="text-xs h-6 px-2">
                        Coming Soon
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default LifestyleFoundations4;